import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Divider,
  Button,
  Toolbar,
} from "@mui/material";
import moment from "moment";
import "react-toastify/dist/ReactToastify.css";
import { useLocation } from "react-router-dom";
import EditWrap from "../../commonComponent/editWrap";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import { BASE_URL, DISABLED_MODULE } from "../../../global";
import { ClassicTable } from "../../../utils/components/Styles";
import { generateAuditPDF } from "./srfauditPrint";
import ReadingAuditPrint from "../../audit/datasheetReadings/readingAuditPrint";

export const mapCalibrationType = (value) => {
  if (value == 1) return "NABL";
  if (value == 2) return "IAS";
  if (value == 3) return "NONABL";
  return value;
};

export const formatValue = (name, value) => {
  if (!value) return "";
  if (name === "calibrationType") {
    return mapCalibrationType(value);
  }
  if (name === "lastModified" || name === "calibrationDate") {
    return moment(value).format("DD-MM-YYYY HH:mm");
  }
  if (typeof value === "string") {
    return value
      .replaceAll("||", ",")
      .replaceAll("|", " to ")
      .replaceAll("#", "  ");
  }
  return value;
};

const SRFAuditView = () => {
  const location = useLocation();
  const [auditData, setAuditData] = useState([]);
  const [tableDataArray, setTableDataArray] = useState([]);
  const printComponentRef = React.useRef();
  const [extraData, setExtraData] = useState({});
  const [editAccess, setEditAccess] = React.useState(
    localStorage.getItem("editAccess")?.split(",").map(Number),
  );

  const queryIdArray = new URLSearchParams(location.search).get("id");
  let AuditIDArray = JSON.parse(queryIdArray);

  useEffect(() => {
    const storedData = sessionStorage.getItem("auditExtraData");
    if (storedData) {
      setExtraData(JSON.parse(storedData) || {});
      // sessionStorage.removeItem("auditExtraData"); // optional: clean up
    }
  }, [auditData]);

  const userMap = extraData?.userList?.reduce((acc, user) => {
    acc[user.id] = user.userName;
    return acc;
  }, {});

  const getData = async (auditId) => {
    try {
      // 🔹 First query (existing logs)
      const data = {
        query: `SELECT al.*, c.companyName, u.userName 
              FROM audit_log al 
              LEFT JOIN clients c ON al.clientId = c.id 
              LEFT JOIN users u ON al.userId = u.id 
              WHERE al.identifier=${auditId} 
              AND (
                al.type = 'srfInstruments' 
                OR (
                  al.type = 'certificates' 
                  AND al.action IN ('download', 'withdraw', 'approve', 'review', 'sign', 'witness')
                )
              )
              ORDER BY 
                CASE 
                  WHEN al.type = 'srfInstruments' AND al.action = 'create' THEN 0 
                  ELSE 1 
                END ASC,
                al.change_date ASC`,
      };

      const res = await axiosWithToken.post(BASE_URL + `dynamic`, data);

      const parsedData = res.data.map((item) => ({
        ...item,
        old_value: item.old_value ? JSON.parse(item.old_value) : null,
        new_value: item.new_value ? JSON.parse(item.new_value) : null,
      }));

      // 🔹 Second query (datasheets logs)
      const dsQuery = {
        query: `SELECT al.*, c.companyName, u.userName 
              FROM audit_log al 
              LEFT JOIN clients c ON al.clientId = c.id 
              LEFT JOIN users u ON al.userId = u.id 
              WHERE al.identifier=${auditId} 
              AND al.type='datasheets' 
              AND al.action='update'
              ORDER BY al.change_date ASC`,
      };

      const dsRes = await axiosWithToken.post(BASE_URL + `dynamic`, dsQuery);

      const keysToCheck = [
        "calibrationDate",
        "nextDueDate",
        "startTemp",
        "endTemp",
        "startHumidity",
        "endHumidity",
      ];

      const firstDataIndex = dsRes.data.findIndex((item) => {
        const newVal = item.new_value ? JSON.parse(item.new_value) : {};

        return keysToCheck.some((key) => {
          const value = newVal[key];
          return value !== null && value !== undefined && value !== "";
        });
      });

      const datasheetLogs = dsRes.data
        .map((item, index) => {
          const oldVal = item.old_value ? JSON.parse(item.old_value) : {};
          const newVal = item.new_value ? JSON.parse(item.new_value) : {};

          let filteredOld = {};
          let filteredNew = {};
          let hasChange = false;

          keysToCheck.forEach((key) => {
            const oldValue = oldVal[key] ?? null;
            const newValue = newVal[key] ?? null;

            // First actual data entry → include all available values
            if (index === firstDataIndex) {
              if (newValue !== null && newValue !== undefined && newValue !== "") {
                filteredNew[key] = newValue;
              }
            } else {
              // Update → only changed values
              if (oldValue !== newValue) {
                filteredOld[key] = oldValue;
                filteredNew[key] = newValue;
                hasChange = true;
              }
            }
          });

          // First entry containing actual data
          if (index === firstDataIndex) {
            return {
              ...item,
              action: "datasheet create",
              new_value: filteredNew,
            };
          }

          // Updates
          if (hasChange) {
            return {
              ...item,
              action: "datasheet update",
              old_value: filteredOld,
              new_value: filteredNew,
            };
          }

          return null;
        })
        .filter(Boolean);

      // 🔹 Merge and sort all logs by change_date
      const finalData = [...parsedData, ...datasheetLogs].sort(
        (a, b) => new Date(a.lastModified) - new Date(b.lastModified),
      );

      return finalData;
    } catch (err) {
      console.log("Data fetching error: ", err);
      throw err;
    }
  };

  useEffect(() => {
    const fetchDataForAllIds = async () => {
      const promises = AuditIDArray.map((auditId) => getData(auditId));

      try {
        const results = await Promise.all(promises);
        setAuditData(results.flat());
      } catch (err) {
        console.log("error", err);
      }
    };

    if (AuditIDArray && AuditIDArray.length > 0) {
      fetchDataForAllIds();
    }
  }, []);

  const generateTableData = (auditData) => {
    const newTableDataArray = [];

    for (const item of auditData) {
      const changeArray = [];

      if (item.new_value) {
        for (const key in item.new_value) {
          if (["id", "srfsId", "instrumentId"].includes(key)) continue;
          changeArray.push({
            name: key,
            preValue: item.old_value ? item.old_value[key] || null : null,
            latestValue: item.new_value[key] || null,
          });
        }
      }

      newTableDataArray.push(changeArray);
    }

    return newTableDataArray;
  };

  useEffect(() => {
    setTableDataArray(generateTableData(auditData));
  }, [auditData]);

  return (
    <EditWrap>
      <Toolbar
        style={{
          padding: "0px",
          overflow: "auto",
          display: "flex",
          justifyContent: "flex-end",
          gap: "20px",
          marginRight: 10,
        }}
      >
        <Button
          variant="contained"
          style={{ backgroundColor: "grey" }}
          size="small"
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.close();
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!(editAccess?.includes(3) || editAccess?.includes(0))}
          onClick={() => {
            generateAuditPDF({
              auditData,
              tableDataArray,
              extraData,
              userMap,
            });
          }}
        >
          Download PDF
        </Button>
      </Toolbar>

      <Typography
        variant="h4"
        sx={{
          textAlign: "center",
          marginBottom: "10px",
          fontWeight: "bold",
          color: "#1a237e",
          textTransform: "uppercase",
        }}
      >
        Audit Trail
      </Typography>

      <Divider sx={{ mb: 2, borderColor: "#1a237e", borderBottomWidth: 2 }} />

      {extraData?.serviceReqNumber && (
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            marginBottom: "20px",
            fontWeight: "medium",
          }}
        >
          <strong>SRF No:</strong> {extraData.serviceReqNumber}
        </Typography>
      )}
      {extraData?.certificatePage && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "16px",
            margin: "20px auto",
            maxWidth: "900px",
          }}
        >
          {[
            {
              label: "Instrument Name",
              value: extraData?.certificatePage?.instrumentName || "",
            },
            ...(extraData?.certificatePage?.requestedname
              ? [
                {
                  label: "Req. DUC Name",
                  value: extraData.certificatePage.requestedname,
                },
              ]
              : []),
            {
              label: "DUCID / Serial Number",
              value:
                extraData?.ducid && extraData?.serialNumber
                  ? `${extraData.ducid} / ${extraData.serialNumber}`
                  : extraData?.ducid || extraData?.serialNumber || "",
            },
            {
              label: "Inward Date",
              value: extraData?.certificatePage?.entryDate
                ? moment(extraData?.certificatePage?.entryDate).format(
                  "DD-MM-YYYY",
                )
                : "",
            },
            // {
            //   label: "Created By",
            //   value: userMap[extraData?.certificatePage?.createdby] || "",
            // },
            {
              label: "Calibrated By",
              value: userMap[extraData?.certificatePage?.calibratedby] || "",
            },
            {
              label: "Calibration Date",
              value: extraData?.certificatePage?.calibrationDate
                ? moment(extraData?.certificatePage?.calibrationDate).format(
                  "DD-MM-YYYY, HH:mm",
                )
                : "",
            },
            {
              label: "Approved By",
              value: userMap[extraData?.certificatePage?.approvedby] || "",
            },
            // {
            //   label: "Approved Date",
            //   value: extraData?.certificatePage?.approvedDate
            //     ? moment(extraData?.certificatePage?.approvedDate).format(
            //         "DD-MM-YYYY, HH:mm"
            //       )
            //     : "",
            // },
          ].map((item, index) => (
            <div key={index}>
              <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                {item.label}
              </Typography>
              <Typography variant="body2">{item.value}</Typography>
            </div>
          ))}
        </div>
      )}

      <div ref={printComponentRef}>
        <div
          style={{
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            maxWidth: "900px",
            gap: "40px",
          }}
        >
          {tableDataArray?.map((tableData, tableIndex) => {
            const action = auditData[tableIndex]?.action?.toLowerCase();
            const changedRows = tableData.filter(
              (column) => column?.preValue !== column?.latestValue,
            );
            if (
              changedRows.length === 0 &&
              ![
                "download",
                "withdraw",
                "approve",
                "review",
                "sign",
                "witness",
              ].includes(action)
            )
              return null;
            return (
              <Paper
                key={tableIndex}
                elevation={4}
                sx={{
                  padding: "20px",
                  borderRadius: "10px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: "#0d47a1",
                    textTransform: "capitalize",
                  }}
                >
                  Action:{" "}
                  {{
                    download: "Certificate Download",
                    withdraw: "Certificate Withdraw",
                    approve: "Certificate Approved",
                    review: "Customer Approval (Reviewed)",
                    sign: "Customer Certificate (Signed)",
                    witness: "Customer Certificate (Witnessed)",
                  }[auditData[tableIndex].action] ||
                    auditData[tableIndex].action}
                </Typography>

                <Divider sx={{ mb: 2 }} />
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 1 }}
                >
                  {[
                    {
                      label: "DUCID / Serial Number",
                      value:
                        extraData?.ducid && extraData?.serialNumber
                          ? `${extraData.ducid} / ${extraData.serialNumber}`
                          : extraData?.ducid || extraData?.serialNumber || "",
                    },
                    { label: "User", value: auditData[tableIndex].userName },
                    {
                      label: "Time",
                      value: moment(auditData[tableIndex].lastModified).format(
                        "DD-MM-YYYY HH:mm",
                      ),
                    },
                  ].map((item, index) => (
                    <Typography
                      variant="h6"
                      key={index}
                      style={{
                        display: "flex",
                        gap: "15px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "bold",
                          width: "50%",
                          textAlign: "left",
                        }}
                      >
                        {item.label}
                      </span>
                      <span style={{ width: "65%", textAlign: "left" }}>
                        : {item.value}
                      </span>
                    </Typography>
                  ))}
                </div>

                {![
                  "download",
                  "withdraw",
                  "approve",
                  "review",
                  "sign",
                  "witness",
                ].includes(action) && (
                    <ClassicTable>
                      <Table
                        stickyHeader
                        aria-label={`Audit Data Table ${tableIndex}`}
                        className="table-responsive"
                        size="small"
                        sx={{ mt: 3 }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <b>Column Name</b>
                            </TableCell>
                            {auditData[tableIndex]?.action !== "create" &&
                              auditData[tableIndex]?.action !==
                              "datasheet create" && (
                                <TableCell>
                                  <b>Previous Value</b>
                                </TableCell>
                              )}
                            <TableCell>
                              <b>
                                {auditData[tableIndex].action == "create" ||
                                  auditData[tableIndex].action == "datasheet create"
                                  ? `Value`
                                  : `Updated Value`}
                              </b>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {changedRows.map((column, columnIndex) => {
                            // console.log("column",column)
                            return (
                              <TableRow key={columnIndex}>
                                <TableCell>{column?.name}</TableCell>
                                {auditData[tableIndex].action !== "create" &&
                                  auditData[tableIndex]?.action !==
                                  "datasheet create" && (
                                    <TableCell>
                                      {formatValue(column?.name, column?.preValue)}
                                    </TableCell>
                                  )}
                                <TableCell>
                                  {formatValue(column?.name, column?.latestValue)}
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </ClassicTable>
                  )}
              </Paper>
            );
          })}
          {!DISABLED_MODULE?.includes("DatasheetReadingsAudit") && (
            <ReadingAuditPrint
              id={extraData?.id}
              instrumentId={extraData?.instrumentId}
              DUCID={
                extraData?.ducid && extraData?.serialNumber
                  ? `${extraData.ducid} / ${extraData.serialNumber}`
                  : extraData?.ducid || extraData?.serialNumber || ""
              }
            />
          )}
        </div>

        <div style={{ width: "100%" }} id="header-section"></div>
      </div>
    </EditWrap>
  );
};

export default SRFAuditView;
